import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserPayments } from '../hooks';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { RefundPaymentModal } from '../components/RefundPaymentModal';
import { ConfirmFaultModal } from '../components/ConfirmFaultModal';
import type { AdminPaymentRow } from '../types';
import { downloadInvoicePdf } from '@/src/infrastructure/lib/api';

export function PaymentsTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const [refundTarget, setRefundTarget] = useState<AdminPaymentRow | null>(null);
  const [faultTarget, setFaultTarget] = useState<AdminPaymentRow | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { data, isLoading, isError, error, refetch } = useUserPayments(userId, { page, limit: 20 });

  const handleDownload = async (invoiceId: string, invoiceNumber?: string | null) => {
    if (downloadingId) return;
    setDownloadingId(invoiceId);
    setDownloadError(null);
    try {
      await downloadInvoicePdf(invoiceId, invoiceNumber);
    } catch (e) {
      setDownloadError((e as Error).message);
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) return <TableSkeleton rows={5} cols={5} />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0)
    return <EmptyState title="No payments" hint="This user has not made any payments." />;

  return (
    <div className="space-y-3">
      {downloadError && (
        <div role="alert" className="text-xs text-red-300">{downloadError}</div>
      )}
      <div className="glass-panel rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-container-low text-[10px] uppercase tracking-widest text-on-surface-variant">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Gateway</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} className="border-t border-border-subtle">
                <td className="px-3 py-2 text-xs">{new Date(p.createdAt).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-xs font-mono">
                  {p.orderNumber ? (
                    p.purchaseId ? (
                      <Link to={`/orders/${p.purchaseId}`} className="underline text-primary">
                        {p.orderNumber}
                      </Link>
                    ) : (
                      p.orderNumber
                    )
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{p.gateway}</td>
                <td className="px-3 py-2">
                  {p.currency} {(p.amount / 100).toFixed(2)}
                  {p.refundedAt && (
                    <div className="text-[10px] text-red-300">
                      refunded {((p.refundAmount ?? 0) / 100).toFixed(2)}
                    </div>
                  )}
                  {p.refundPending && !p.refundedAt && (
                    <div className="text-[10px] text-amber-400">refund pending confirmation</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs uppercase">
                  {p.status === 'REFUNDED' || (p.refundAmount && p.refundAmount >= p.amount) ? (
                    <span className="text-red-400 font-semibold">REFUNDED</span>
                  ) : (
                    p.status
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{p.plan}</td>
                <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                  {p.status === 'PAID' && (
                    <button
                      onClick={() => void handleDownload(p.id, p.invoiceNumber)}
                      disabled={downloadingId !== null}
                      aria-label={`Download invoice ${p.invoiceNumber ?? p.id}`}
                      className="px-3 py-1 rounded-md text-[10px] uppercase tracking-widest font-bold border border-border-subtle hover:bg-surface-container-low disabled:opacity-40"
                    >
                      {downloadingId === p.id ? 'Preparing…' : 'Invoice'}
                    </button>
                  )}
                  {p.status === 'PAID' && !p.refundedAt && !p.refundPending && (
                    <button
                      onClick={() => setFaultTarget(p)}
                      className="px-3 py-1 rounded-md text-[10px] uppercase tracking-widest font-bold border border-amber-400/40 text-amber-300 hover:bg-amber-500/10"
                    >
                      Confirm fault
                    </button>
                  )}
                  {p.status === 'PAID' && !p.refundedAt && !p.refundPending && (
                    <button
                      onClick={() => setRefundTarget(p)}
                      className="px-3 py-1 rounded-md text-[10px] uppercase tracking-widest font-bold border border-red-400/40 text-red-300 hover:bg-red-500/10"
                    >
                      Refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2 text-xs">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40">Prev</button>
        <span className="px-2 py-1.5 text-on-surface-variant">Page {page} / {data.meta.totalPages || 1}</span>
        <button disabled={page >= (data.meta.totalPages || 1)} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40">Next</button>
      </div>

      {faultTarget && (
        <ConfirmFaultModal open={!!faultTarget} payment={faultTarget} onClose={() => setFaultTarget(null)} />
      )}
      {refundTarget && (
        <RefundPaymentModal open={!!refundTarget} payment={refundTarget} onClose={() => setRefundTarget(null)} />
      )}
    </div>
  );
}
